<template>
  <div style="width: 100%; height: 100%">
    <attribute-filter-panel
      title-localisation="attributes.filters.filterByAttributes"
      filter-count-localisation="attributes.filters.numApplied"
      :enable-tool-store-group-filter="false"
      :enable-similarity-group-filter="false"
      :enable-cann-group-filter="false"
      :filter-rules="retailAttributesFilter"
      @attributeFilterChange="setFilterRules"
    />
    <div class="d-flex similarity-groups-actions">
      <!-- Generate Switching Matrix button -->
      <div
        v-if="isSwitchingServiceEnabled && !isWeeklyWorkpackage && hasAOExtracts"
        class="workflow-controls"
      >
        <div class="d-flex workflow-buttons">
          <tooltip
            class="switching-status-tooltip"
            :value="generateSwitchingMatrixTooltipMsg"
            :disabled="!generateSwitchingMatrixTooltipMsg"
            position="bottom"
          >
            <v-btn
              color="primary"
              small
              depressed
              :disabled="!canGenerateSwitchingMatrix"
              :loading="runningSwitching"
              @click="generateSwitchingMatrix"
            >
              {{ $tkey('actions.generateSwitchingMatrix') }}
              <v-icon v-once size="2rem">mdi-matrix</v-icon>
            </v-btn>
          </tooltip>
        </div>
      </div>

      <div class="workflow-controls">
        <div class="d-flex workflow-buttons">
          <!-- Refresh SG button -->
          <tooltip
            :value="refreshSimGroupsTooltipMsg"
            :disabled="!refreshSimGroupsTooltipMsg"
            position="bottom"
          >
            <v-menu v-if="isSwitchingServiceEnabled" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  small
                  depressed
                  :disabled="!canRefreshSimilarityGroups"
                  :loading="runningRefresh"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ $tkey('actions.refreshSimilarityGroups') }}
                  <v-icon size="2rem">mdi-menu-down</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="refreshSimilarityGroups(false)">
                  <v-list-item-title>{{
                    $tkey('actions.refreshSimilarityGroups')
                  }}</v-list-item-title>
                </v-list-item>
                <tooltip
                  :value="refreshSimilarityGroupUseProductSwitchingTooltipMsg"
                  :disabled="!refreshSimilarityGroupUseProductSwitchingTooltipMsg"
                  position="right"
                >
                  <v-list-item
                    :disabled="!hasGeneratedSwitchingMatrix"
                    @click="refreshSimilarityGroups(true)"
                  >
                    <v-list-item-title>{{
                      $tkey('actions.refreshSimilarityGroupsUseProductSwitching')
                    }}</v-list-item-title>
                  </v-list-item>
                </tooltip>
              </v-list>
            </v-menu>
            <v-btn
              v-else
              color="primary"
              small
              depressed
              :disabled="!canRefreshSimilarityGroups"
              :loading="runningRefresh"
              @click="refreshSimilarityGroups(false)"
            >
              {{ $tkey('actions.refreshSimilarityGroups') }}
              <v-icon v-once size="2rem">mdi-refresh</v-icon>
            </v-btn>
          </tooltip>
        </div>
        <div class="workflow-info-row">
          <last-triggered-info
            :last-triggered-info="lastTriggeredInfo"
            :loading="isLastTriggeredLoading"
            translation-time-key="settings.similarityGroups.lastRefreshTime"
            class="mr-0"
          />
        </div>
      </div>

      <div class="workflow-controls">
        <div class="d-flex workflow-buttons">
          <!-- Refresh XEO Params button -->
          <tooltip
            :value="refreshParamsTooltipMsg"
            :disabled="!refreshParamsTooltipMsg"
            position="bottom"
          >
            <v-menu v-if="isSwitchingServiceEnabled" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  small
                  depressed
                  :disabled="!canRefreshXEOParams"
                  :loading="runningXEORefresh"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ $tkey('actions.refreshXEOParams') }}
                  <v-icon size="2rem">mdi-menu-down</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="refreshXEOParams(false)">
                  <v-list-item-title>{{ $tkey('actions.refreshXEOParams') }}</v-list-item-title>
                </v-list-item>
                <tooltip
                  :value="refreshXEOParamsUseProductSwitchingTooltipMsg"
                  :disabled="!refreshXEOParamsUseProductSwitchingTooltipMsg"
                  position="right"
                >
                  <v-list-item
                    :disabled="!hasGeneratedSwitchingMatrix"
                    @click="refreshXEOParams(true)"
                  >
                    <v-list-item-title>{{
                      $tkey('actions.refreshXEOParamsUseProductSwitching')
                    }}</v-list-item-title>
                  </v-list-item>
                </tooltip>
              </v-list>
            </v-menu>
            <v-btn
              v-else
              color="primary"
              small
              depressed
              :disabled="!canRefreshXEOParams"
              :loading="runningXEORefresh"
              @click="refreshXEOParams(false)"
            >
              {{ $tkey('actions.refreshXEOParams') }}
              <v-icon v-once size="2rem">mdi-refresh</v-icon>
            </v-btn>
          </tooltip>
        </div>
        <div class="workflow-info-row">
          <last-triggered-info
            :last-triggered-info="lastTriggeredXEOInfo"
            :loading="isLastTriggeredLoading"
            translation-time-key="settings.similarityGroups.lastRefreshTime"
            class="mr-0"
          />
        </div>
      </div>

      <similarity-groups-upload :disabled="isMasterWorkpackage || isWeeklyWorkpackage" />
      <v-btn color="primary" small depressed @click="exportSimilarityGroups">
        <v-icon small>$export</v-icon>
        {{ $t('actions.download') }}
      </v-btn>
      <v-btn
        class="inputs-actions__columns grid-btn pa-0"
        secondary
        small
        depressed
        @click="toggleSideBar('columns')"
      >
        <v-icon class="mr-1">mdi-view-column-outline</v-icon>
        {{ $t('inputs.columns') }}
      </v-btn>
    </div>

    <!-- Table -->
    <ag-grid-vue
      :style="{ width: '100%', height: `70rem` }"
      class="ag-theme-custom ag-similarity-groups-grid"
      :grid-options="gridOptions"
      :column-defs="columnDefs"
      :fill-operation="fillOperation"
      :side-bar="sideBar"
      @grid-ready="onGridReady"
      @column-visible="onColumnVisible"
    />

    <div class="mt-3 mr-4 text-right">
      <v-btn
        class="grid-btn mr-2"
        secondary
        small
        depressed
        :disabled="!editsExist || isSaving"
        @click="onDiscard"
      >
        {{ $t('actions.discard') }}
      </v-btn>

      <v-btn
        color="success"
        small
        depressed
        :loading="isSaving"
        :disabled="!editsExist || isSaving"
        @click="onSave"
      >
        {{ $t('actions.save') }}
      </v-btn>
    </div>

    <!-- Edit Similarity Group dialog -->
    <common-dialog
      :is-open="isEditDialogOpen"
      :header-text="$tkey('headers.editGroupName')"
      :ok-btn-text="$t('actions.save')"
      :ok-btn-disabled="!isEditFormValid"
      @onOk="openEditConfirm"
      @onCancel="cancelEdit"
    >
      <template #body>
        <v-form ref="editForm" v-model="isEditFormValid">
          <v-row no-gutters class="mt-4">
            <v-col cols="12">
              <v-autocomplete
                v-model="selectedSimilarityGroupKey"
                :items="similarityGroupOptions"
                :label="$tkey('actions.selectSimilarityGroup')"
                dense
                :rules="[valueRequired]"
              />
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12">
              <v-text-field
                v-model.trim="newSimilarityGroupName"
                :label="$tkey('actions.changeToNewName')"
                :rules="[valueRequired, isNameUnique]"
              />
            </v-col>
          </v-row>
        </v-form>
      </template>
    </common-dialog>

    <!-- Edit Similarity Group confirm save dialog -->
    <confirm-dialog
      :is-open="isEditConfirmDialogOpen"
      :ok-btn-text="$t('actions.yesProceed')"
      @onOk="confirmEdit"
      @onCancel="cancelEdit"
    >
      <template v-slot:body>
        <div>
          {{ $tkey('dialogs.editConfirm', { group: selectedSimilarityGroupName }) }}
        </div>
        <div>{{ $tkey('dialogs.areYouSureToProceed') }}</div>
      </template>
    </confirm-dialog>

    <!-- Create Similarity Group dialog -->
    <common-dialog
      :is-open="isCreateDialogOpen"
      :header-text="$tkey('dialogs.createTitle')"
      :ok-btn-text="$t('actions.create')"
      :ok-btn-disabled="!isCreateFormValid"
      @onOk="confirmCreate"
      @onCancel="cancelCreate"
    >
      <template #body>
        <v-form v-if="createDialogProduct" ref="createForm" v-model="isCreateFormValid">
          <v-row no-gutters class="mt-2">
            <v-col cols="12">
              <div class="text-subtitle-2 mb-2">{{ $tkey('dialogs.productContext') }}</div>
              <div class="ml-4">
                <div>
                  <strong>{{ $tkey('headers.productKeyDisplay') }}:</strong>
                  {{ createDialogProduct.productKeyDisplay }}
                </div>
                <div>
                  <strong>{{ $tkey('dialogs.cannGroupLabel') }}:</strong>
                  {{ createDialogProduct.cannGroup.cannGroupName }}
                </div>
                <div>
                  <strong>{{ $tkey('dialogs.uomLabel') }}:</strong>
                  {{ createDialogProduct.contentUnitOfMeasure }}
                </div>
              </div>
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-4">
            <v-col cols="12">
              <v-text-field
                v-model.trim="newGroupName"
                :label="$tkey('dialogs.enterName')"
                :rules="[valueRequired, isCreateNameUnique]"
                autofocus
              />
            </v-col>
          </v-row>
        </v-form>
      </template>
    </common-dialog>
  </div>
</template>

<script>
/* eslint-disable no-restricted-syntax */
import { AgGridVue } from 'ag-grid-vue';
import 'ag-grid-enterprise';
import moment from 'moment';
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import axios from 'axios';
import to from 'await-to-js';
import {
  isEmpty,
  get,
  toLower,
  trim,
  sortBy,
  cloneDeep,
  isNil,
  size,
  map,
  keyBy,
  startCase,
} from 'lodash';
import featureFlagsMixin from '../../../mixins/featureFlags';
import tablesMixin from '../../../mixins/tables';
import inputScreensTableMixin from '../../../mixins/inputScreensTable';
import sideBarMixin from '../../../mixins/sideBar';
import {
  displayClientProductKey,
  maxAdditionalGridViewAttributes,
  allowGhostProducts,
  enableProductSwitchingService,
} from '@enums/feature-flags';
import { pricingGroupLevel } from '@enums/hierarchy';
import Events from '@enums/events';
import { descending } from '@enums/sort-direction';
import { hoursMinutesDayMonthYearSeparatedFormat } from '@enums/date-formats';
import workpackagePeriodTypes from '@enums/workpackage-period-types';
import agMenuHeader from '../../../components/ag-grid-cell-renderers/ag-menu-header.vue';
import { formatFilters } from '../../../store/utils/filters';
import SimilarityGroupsUpload from './similarity-groups-upload.vue';

const formatStatusLabel = status => startCase(String(status || '').replace(/_/g, ' ')).trim();

/** Maps a cann group status to an emoji for tooltip display. */
const getStatusIcon = status => {
  const s = String(status || '').toLowerCase();
  if (s === 'completed') return '✅';
  if (s === 'failed') return '😵';
  if (s === 'in-progress') return '⚙️';
  return '📩'; // pending, or any unrecognised state
};

// Polling interval for switching job status updates (30 seconds)
const switchingPollingIntervalMs = 30 * 1000;

export default {
  name: 'SimilarityGroups',

  localizationKey: 'settings.similarityGroups',

  components: {
    AgGridVue,
    // eslint-disable-next-line vue/no-unused-components
    agMenuHeader,
    SimilarityGroupsUpload,
  },

  mixins: [featureFlagsMixin, tablesMixin, inputScreensTableMixin, sideBarMixin],

  data() {
    return {
      products: [],
      originalProducts: [],
      stateDiff: {},
      isLoadingData: false,
      isLastTriggeredLoading: false,
      isSaving: false,
      lastTriggeredInfo: {},
      lastTriggeredXEOInfo: {},
      runningRefresh: false,
      runningXEORefresh: false,
      runningSwitching: false,
      gridApi: null,
      gridColumnApi: null,
      gridReadyPromise: null,
      pricingGroupLevel,
      gridOptions: {
        enableFillHandle: true,
        singleClickEdit: true,
        fillHandleDirection: 'y',
        enableRangeSelection: true,
        accentedSort: true,
        tooltipShowDelay: 200,
        defaultColDef: {
          resizable: true,
          filter: 'agMultiColumnFilter',
          headerClass: 'ag-product-list-grid__header',
          minWidth: 25,
          menuTabs: ['filterMenuTab', 'generalMenuTab'],
          suppressMovable: true,
          suppressFillHandle: true,
        },
        rowHeight: 28,
        headerHeight: 27,
        overlayNoRowsTemplate: `<span class="ag-overlay-loading-center">${this.$t(
          'grid.applyFilter'
        )}</span>`,
        overlayLoadingTemplate: `<span class="ag-overlay-loading-center">${this.$t(
          'grid.loading'
        )}</span>`,
        excelStyles: [
          {
            id: 'stringType',
            dataType: 'String',
          },
        ],
      },
      isEditDialogOpen: false,
      isEditConfirmDialogOpen: false,
      selectedSimilarityGroupKey: '',
      newSimilarityGroupName: '',
      isEditFormValid: true,
      isCreateDialogOpen: false,
      createDialogProduct: null,
      newGroupName: '',
      isCreateFormValid: true,
      enableProductSwitchingService,
      switchingPollingTimerId: null,
      latestSwitchingJobStatus: null,
      switchingCannGroupStatuses: [],
    };
  },

  computed: {
    ...mapState('clientConfig', ['toggleLogic']),
    ...mapState('context', ['profile']),
    ...mapState('filters', ['retailAttributesFilter']),
    ...mapState('workpackages', ['selectedWorkpackage']),
    ...mapGetters('attributes', [
      'getGridDefaultAttributes',
      'getGridNonDefaultAttributes',
      'getGridDisplayedAttributes',
    ]),
    ...mapGetters('workpackages', ['getSelectedWorkpackage', 'isMasterWorkpackage']),

    columnDefs() {
      return [
        {
          headerName: this.$tkey('headers.productInfo'),
          groupId: 'productInfo',
          marryChildren: true,
          pinned: 'left',
          children: this.productInfoColumns,
        },
        {
          headerName: this.$tkey('headers.attributes', {
            max: this.toggleLogic[maxAdditionalGridViewAttributes],
          }),
          groupId: 'attributes',
          marryChildren: true,
          children: this.attributeColumns,
        },
        {
          headerName: this.$tkey('headers.similarityGroupInfo'),
          groupId: 'similarityGroups',
          marryChildren: true,
          children: this.similarityGroupColumns,
        },
        {
          headerName: this.$tkey('headers.xeoParameters'),
          groupId: 'xeoParameters',
          marryChildren: true,
          children: this.xeoParameterColumns,
        },
      ];
    },
    productInfoColumns() {
      return [
        {
          headerName: this.$tkey('headers.productKeyDisplay'),
          field: 'productKeyDisplay',
          width: 110,
          cellClass: 'stringType',
        },
        {
          headerName: this.$tkey('headers.clientProductKey'),
          field: 'clientProductKey',
          hide: !this.isFeatureFlagEnabled(displayClientProductKey),
          width: 110,
          cellClass: 'stringType',
        },
        {
          headerName: this.$tkey('headers.productDescription'),
          field: 'productName',
          width: 200,
          cellClass: ['stringType', 'font-weight-bold'],
        },
        {
          headerName: this.$tkey('headers.productSizeType'),
          field: 'productSizeType',
          width: 100,
          cellClass: 'stringType',
        },
        {
          headerName: this.$tkey('headers.pricingGroup'),
          field: 'pricingGroup',
          width: 150,
          cellClass: 'stringType',
        },
      ];
    },
    similarityGroupColumns() {
      return [
        {
          headerName: this.$tkey('headers.cannGroupName'),
          field: 'cannGroup.cannGroupName',
          width: 150,
          cellClass: ['stringType'],
        },
        {
          headerName: this.$tkey('headers.similarityGroupDescription'),
          headerComponent: 'agMenuHeader',
          headerComponentParams: {
            enableMenu: true,
            enableSorting: true,
            menuOptions: [
              {
                text: this.$tkey('actions.editGroupName'),
                itemClick: this.openEditDialog,
                disabled: this.isMasterWorkpackage,
              },
            ],
          },
          field: 'similarityGroup.similarityGroupKey',
          suppressFillHandle: false,
          width: 150,
          cellClassRules: {
            'diff-background': this.hasDiff,
          },
          // If a product does not have a similarity or cann group, then do not allow reassignment.
          // Do not allow editing SGs in master workpackage (SGs can be transferred to master from other WPs)
          editable: params =>
            get(params.data, 'cannGroup.cannGroupId') &&
            get(params.data, 'similarityGroup.similarityGroupKey') &&
            !this.isMasterWorkpackage &&
            !this.isWeeklyWorkpackage,
          tooltipValueGetter: params => {
            if (!get(params.data, 'cannGroup.cannGroupId')) {
              return this.$tkey('missingCannGroupHint');
            }
          },
          cellEditor: 'agRichSelectCellEditor',
          cellEditorParams: ({ data }) => {
            const existingGroups =
              this.reassignSGOptions[
                `${data.cannGroup.cannGroupId}::${data.contentUnitOfMeasure}`
              ] || [];
            // Capture component context for use in renderer
            const vm = this;
            const values = [...existingGroups, '__CREATE_NEW__'];
            return {
              values,
              cellRenderer: params => {
                if (params.value === '__CREATE_NEW__') {
                  return vm.$tkey('dropdown.createNew');
                }
                // Dynamically fetch from computed property to get latest data
                return get(vm.similarityGroups[params.value], 'similarityGroupDescription', '');
              },
            };
          },
          onCellValueChanged: this.handleSimilarityGroupChange,
          valueSetter: params => {
            const newKey = params.newValue;
            const oldKey = get(params.data, 'similarityGroup.similarityGroupKey');

            // Block __CREATE_NEW__ from being stored
            if (newKey === '__CREATE_NEW__') {
              // Open dialog immediately using $nextTick to ensure it runs after AG-Grid processing
              this.$nextTick(() => {
                this.openCreateDialog(params.data);
              });
              return false; // Don't update the cell value
            }

            // Handle regular reassignments
            if (newKey !== oldKey) {
              const sgInfo = this.similarityGroups[newKey];

              if (sgInfo) {
                // Update grid data
                params.data.similarityGroup = { ...sgInfo };

                // Update this.products array to keep it in sync
                const productIndex = this.products.findIndex(
                  p => p.productKey === params.data.productKey
                );
                if (productIndex !== -1) {
                  this.$set(this.products[productIndex], 'similarityGroup', { ...sgInfo });
                }
              }
            }
            return true; // Allow the change
          },
          valueParser: params => get(params.data, 'similarityGroup.similarityGroupKey'),
          valueFormatter: params =>
            get(this.similarityGroups[params.value], 'similarityGroupDescription', ''),
          filterValueGetter: params =>
            get(
              this.similarityGroups[get(params.data, 'similarityGroup.similarityGroupKey')],
              'similarityGroupDescription',
              ''
            ),
        },
        {
          headerName: this.$tkey('headers.isGhost'),
          field: 'isGhost',
          width: 100,
          cellClass: ['stringType'],
          hide: !this.isGhostProductsEnabled,
          valueFormatter: params => {
            return params.value ? this.$t('general.yes') : this.$t('general.no');
          },
        },
      ];
    },

    xeoParameterColumns() {
      return [
        {
          headerName: this.$tkey('headers.alpha'),
          field: 'parameters.alpha',
          colId: 'parameters.alpha',
          hide: true,
          width: 130,
        },
        {
          headerName: this.$tkey('headers.beta'),
          field: 'parameters.beta',
          colId: 'parameters.beta',
          hide: true,
          width: 130,
        },
      ];
    },

    originalProductsByKey() {
      return keyBy(this.originalProducts, 'productKey');
    },

    attributeColumns() {
      return [
        ...this.getGridDefaultAttributes.map(defaultAttribute => ({
          headerName: this.$t(`pricing.attributes.${defaultAttribute.attributeDescription}`),
          valueGetter: params => this.getAttributeValue(params, defaultAttribute),
          colId: `attributes.${defaultAttribute.attributeKey}`,
          width: 80,
          ...this.getAttributeColumnVisibilityState(defaultAttribute.attributeKey, {
            isDefault: true,
          }),
        })),
        ...this.getGridNonDefaultAttributes.map(nonDefaultAttribute => ({
          headerName: nonDefaultAttribute.displayDescription,
          valueGetter: params => this.getAttributeValue(params, nonDefaultAttribute),
          colId: `attributes.${nonDefaultAttribute.attributeKey}`,
          width: 80,
          ...this.getAttributeColumnVisibilityState(nonDefaultAttribute.attributeKey),
        })),
      ];
    },

    hasAOExtracts() {
      return !isEmpty(get(this.getSelectedWorkpackage, 'aoExtracts', []));
    },

    hasSimilarityGroups() {
      return this.hasAOExtracts && !isEmpty(this.similarityGroupOptions);
    },

    hasGeneratedSwitchingMatrix() {
      return this.switchingCannGroupStatuses.some(cannGroup => cannGroup.status === 'completed');
    },

    anyWorkflowActionRunning() {
      return this.runningSwitching || this.runningRefresh || this.runningXEORefresh;
    },

    canGenerateSwitchingMatrix() {
      return !this.isWeeklyWorkpackage && this.hasAOExtracts && !this.anyWorkflowActionRunning;
    },

    canRefreshSimilarityGroups() {
      return !this.isWeeklyWorkpackage && this.hasAOExtracts && !this.anyWorkflowActionRunning;
    },

    canRefreshXEOParams() {
      return (
        !this.isWeeklyWorkpackage && this.hasSimilarityGroups && !this.anyWorkflowActionRunning
      );
    },

    isWeeklyWorkpackage() {
      return (
        get(this.selectedWorkpackage, 'workpackagePeriodType') === workpackagePeriodTypes.weekly
      );
    },

    isGhostProductsEnabled() {
      const ghostFlags = this.toggleLogic[allowGhostProducts];
      return this.isWeeklyWorkpackage ? ghostFlags.weekly : ghostFlags.aoPeriod;
    },

    similarityGroups() {
      const result = this.products.reduce((acc, product) => {
        const {
          similarityGroup: { similarityGroupKey, similarityGroupDescription, cluster } = {},
        } = product;
        // Filter out both falsy values and the special __CREATE_NEW__ placeholder
        if (similarityGroupKey && similarityGroupKey !== '__CREATE_NEW__') {
          acc[similarityGroupKey] = {
            similarityGroupKey,
            similarityGroupDescription,
            cluster,
          };
        }
        return acc;
      }, {});
      return result;
    },

    similarityGroupOptions() {
      const options = Object.keys(this.similarityGroups).map(key => ({
        text: get(this.similarityGroups[key], 'similarityGroupDescription'),
        value: key,
      }));
      return sortBy(options, option => toLower(option.text));
    },

    reassignSGOptions() {
      // User can reassign products to existing similarity groups
      // as long as they are within the same cann group and have the same unit of measure.
      // A single cann group can have multiple similarity groups and multiple UOMs (contentUnitOfMeasure).
      // However, similarity group can belong to only 1 cann-group and contain products from only 1 UOM
      return this.products.reduce(
        (
          acc,
          {
            cannGroup: { cannGroupId } = {},
            contentUnitOfMeasure,
            similarityGroup: { similarityGroupKey } = {},
          }
        ) => {
          if (cannGroupId && contentUnitOfMeasure && similarityGroupKey) {
            const key = `${cannGroupId}::${contentUnitOfMeasure}`;
            if (!acc[key]) {
              acc[key] = [];
            }
            if (!acc[key].includes(similarityGroupKey)) {
              acc[key].push(similarityGroupKey);
            }
          }
          return acc;
        },
        {}
      );
    },

    selectedSimilarityGroupName() {
      return get(
        this.similarityGroups[this.selectedSimilarityGroupKey],
        'similarityGroupDescription'
      );
    },

    cannGroupIdToNameMap() {
      return this.products.reduce((acc, product) => {
        const { cannGroup: { cannGroupId, cannGroupName } = {} } = product;
        if (cannGroupId) {
          acc[String(cannGroupId)] = cannGroupName || cannGroupId;
        }
        return acc;
      }, {});
    },

    refreshSimGroupsTooltipMsg() {
      if (this.isWeeklyWorkpackage) {
        return this.$t('gridView.tooltips.notAllowedInWeeklyWorkpackage');
      }
      if (!this.hasAOExtracts) {
        return this.$t('gridView.tooltips.missingAOExtracts');
      }
      if (this.runningSwitching) {
        return this.$t('gridView.tooltips.switchingInProgress');
      }
      if (this.runningXEORefresh) {
        return this.$t('gridView.tooltips.xeoRefreshInProgress');
      }
      if (this.runningRefresh) {
        return this.$t('gridView.tooltips.similarityGroupRefreshInProgress');
      }
      return '';
    },

    refreshSimilarityGroupUseProductSwitchingTooltipMsg() {
      if (!this.hasGeneratedSwitchingMatrix) {
        return this.$t('gridView.tooltips.missingSwitchingMatrix');
      }
      return '';
    },

    refreshXEOParamsUseProductSwitchingTooltipMsg() {
      if (!this.hasGeneratedSwitchingMatrix) {
        return this.$t('gridView.tooltips.missingSwitchingMatrix');
      }
      return '';
    },

    refreshParamsTooltipMsg() {
      if (this.isWeeklyWorkpackage) {
        return this.$t('gridView.tooltips.notAllowedInWeeklyWorkpackage');
      }
      if (this.runningSwitching) {
        return this.$t('gridView.tooltips.switchingInProgress');
      }
      if (this.runningRefresh) {
        return this.$t('gridView.tooltips.similarityGroupRefreshInProgress');
      }
      if (this.runningXEORefresh) {
        return this.$t('gridView.tooltips.xeoRefreshInProgress');
      }
      if (!this.hasSimilarityGroups) {
        return this.$t('gridView.tooltips.missingSGs');
      }
      return '';
    },

    generateSwitchingMatrixTooltipMsg() {
      if (this.isWeeklyWorkpackage) {
        return this.$t('gridView.tooltips.notAllowedInWeeklyWorkpackage');
      }
      if (!this.hasAOExtracts) {
        return this.$t('gridView.tooltips.missingAOExtracts');
      }
      if (this.runningRefresh) {
        return this.$t('gridView.tooltips.similarityGroupRefreshInProgress');
      }
      if (this.runningXEORefresh) {
        return this.$t('gridView.tooltips.xeoRefreshInProgress');
      }

      // Dynamically count groups by status
      const statusCounts = {};
      this.switchingCannGroupStatuses.forEach(cg => {
        const status = cg.status || 'unknown';
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });

      const summaryParts = Object.entries(statusCounts)
        .map(([status, count]) => `${formatStatusLabel(status)}: ${count}`)
        .join(' | ');
      const summaryHeader = `Total: ${this.switchingCannGroupStatuses.length} | ${summaryParts}`;

      const statusLines = this.switchingCannGroupStatuses
        .map(cannGroup => {
          const cannGroupName =
            cannGroup.cannGroupName ||
            this.cannGroupIdToNameMap[String(cannGroup.cannGroupId)] ||
            cannGroup.cannGroupId;
          return `${getStatusIcon(cannGroup.status)} ${cannGroupName}`;
        })
        .join('\n');

      if (this.runningSwitching && statusLines) {
        return `${this.$t(
          'gridView.tooltips.switchingInProgress'
        )}\n${summaryHeader}\n\n${statusLines}`;
      }

      if (this.runningSwitching) {
        return `${this.$t('gridView.tooltips.switchingInProgress')}\n${summaryHeader}`;
      }

      if (statusLines) {
        return `${summaryHeader}\n\n${statusLines}`;
      }

      return '';
    },
    isSwitchingServiceEnabled() {
      return this.isFeatureFlagEnabled(enableProductSwitchingService);
    },

    editsExist() {
      return size(this.stateDiff);
    },
    sideBar() {
      return {
        toolPanels: [this.columnToolPanel],
        hiddenByDefault: true,
      };
    },
  },

  mounted() {
    // Listen for the refresh events
    this.$eventBus.$on('refreshSimilarityGroupsFinished', params => {
      this.postRefresh(params, 'run_refresh_similarity_groups');
    });
    this.$eventBus.$on('refreshXeoParamsFinished', params => {
      this.postRefresh(params, 'refresh_cross_elasticity_parameters');
    });
    this.$eventBus.$on('updateSimilarityGroupsImportFinished', () => {
      this.fetchGridData();
    });
  },

  beforeDestroy() {
    this.clearSwitchingPollingTimer();
    // Unsubscribe from the event to prevent memory leaks
    this.$eventBus.$off('refreshSimilarityGroupsFinished');
    this.$eventBus.$off('refreshXeoParamsFinished');
    this.$eventBus.$off('updateSimilarityGroupsImportFinished');
  },

  async created() {
    const defaultAttributes = this.getGridDefaultAttributes.map(({ attributeKey }) => attributeKey);
    this.setGridDisplayedAttributes(new Set(defaultAttributes));
    this.additionalAttributes = new Set(defaultAttributes);

    // Check if refresh is currently running
    const { isRefreshRunning, isXEORefreshRunning } = await this.isRefreshRunning();
    if (!isRefreshRunning && !isXEORefreshRunning) {
      // Fetch product data for grid
      this.fetchGridData();
    }
    this.fetchLastTriggeredInfo();
    this.fetchAttributeMetadata();
    if (this.isSwitchingServiceEnabled) {
      await this.fetchSwitchingCannGroupStatuses();
      // Resume polling if active jobs are detected from cann group statuses
      await this.resumeSwitchingPollingIfNeeded();
    }
  },

  methods: {
    ...mapActions('attributes', ['fetchAttributeMetadata']),
    ...mapActions('filters', ['setSelectedFilter']),
    ...mapMutations('attributes', ['setGridDisplayedAttributes']),
    ...mapMutations('notifications', ['addNotification']),

    /* -------------------------------------------------- *
     *           Utility functions                        *
     * -------------------------------------------------- */
    /**
     * Generate hash for similarity group key (truncated SHA-256 to match MD5 length)
     * @param {string} input - String to hash
     * @returns {Promise<string>} 32-character hash (truncated SHA-256)
     */
    async generateSimilarityGroupHash(input) {
      const data = new TextEncoder().encode(input);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const fullHash = [...new Uint8Array(hashBuffer)]
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
      // Truncate SHA-256 to 32 chars to match existing MD5-based similarity group keys without adding a crypto library
      return fullHash.substring(0, 32);
    },

    /**
     * Count unique similarity groups for a cannGroup/UOM combination
     * Returns the count + 1 to use as cluster value for new groups
     * @param {number} cannGroupId - Cann group ID
     * @param {string} uom - Unit of measure
     * @returns {number} Count of unique similarity groups + 1
     */
    countUniqueSimilarityGroups(cannGroupId, uom) {
      const uniqueKeys = new Set();

      this.products.forEach(product => {
        if (
          get(product, 'cannGroup.cannGroupId') === cannGroupId &&
          product.contentUnitOfMeasure === uom &&
          get(product, 'similarityGroup.similarityGroupKey')
        ) {
          uniqueKeys.add(get(product, 'similarityGroup.similarityGroupKey'));
        }
      });

      return uniqueKeys.size + 1;
    },

    /**
     * Check if any cann groups have unterminated status (not completed or failed).
     * Used to detect active polling jobs after page reload.
     * @returns {boolean} true if any cann groups are still in progress
     */
    hasUnterminatedCannGroups() {
      return this.switchingCannGroupStatuses.some(
        cg => cg.status && !['completed', 'failed'].includes(cg.status.toLowerCase())
      );
    },

    /**
     * Get the active job ID from the first unterminated cann group.
     * Returns null if no active jobs are found.
     * @returns {string|null} jobId of the active job, or null if none found
     */
    getActiveJobIdFromCannGroups() {
      const unfinishedGroups = this.switchingCannGroupStatuses.filter(
        cg => cg.status && !['completed', 'failed'].includes(cg.status.toLowerCase())
      );

      if (unfinishedGroups.length > 0) {
        // Get the job ID from the first unterminated group (groups are sorted by updatedAt desc)
        return get(unfinishedGroups[0], 'jobId') || null;
      }

      return null;
    },

    /* -------------------------------------------------- *
     *           Grid / Data loading functions            *
     * -------------------------------------------------- */
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridApi.showLoadingOverlay();
      this.gridColumnApi = params.columnApi;

      // Grid now ready to be used.
      if (this.gridReadyPromise) {
        this.gridReadyPromise(true);
      }
    },
    async fetchGridData() {
      // Show loading state
      this.isLoadingData = true;
      if (this.gridApi) {
        this.gridApi.showLoadingOverlay();
      }

      try {
        // Fetch similarity groups
        const { data } = await axios.get(`/api/similarity-groups/${this.selectedWorkpackage._id}`, {
          params: {
            attributeKeys: Array.from(this.getGridDisplayedAttributes),
            where: formatFilters({
              where: this.retailAttributesFilter,
              rootState: this.$store.state,
            }),
          },
        });

        // Grid might not be ready when data is here. If so, wait.
        if (!this.gridApi) {
          await new Promise(resolve => {
            this.gridReadyPromise = resolve;
          });
        }

        // Set grid data once grid is ready
        this.setGridData(data);

        if (!isEmpty(data)) {
          this.gridApi.hideOverlay();
        } else {
          this.gridApi.showNoRowsOverlay();
        }
      } catch (error) {
        console.error('Error fetching grid data:', error);
        // Handle error and hide loading overlay
        if (this.gridApi) {
          this.gridApi.hideOverlay();
        }
      } finally {
        this.isLoadingData = false;
      }
    },

    setGridData(data) {
      this.stateDiff = {};
      this.products = data;
      this.originalProducts = cloneDeep(data);
      this.gridApi.setGridOption('rowData', data);
    },

    /* -------------------------------------------------- *
     *           Grid / Data Refresh functions            *
     * -------------------------------------------------- */
    async refreshSimilarityGroups(useProductSwitching = false) {
      this.gridApi.showLoadingOverlay();
      this.runningRefresh = true;
      const [err] = await to(
        axios.post(`/api/similarity-groups/${this.selectedWorkpackage._id}/refresh`, {
          use_product_switching_matrices: useProductSwitching,
        })
      );

      if (err) {
        this.runningRefresh = false;
        this.gridApi.hideOverlay();
      }
    },

    async postRefresh({ payload: workpackageId }, jobType) {
      const selectedWorkpackageId = this.selectedWorkpackage._id || workpackageId;
      // Ensure that response matches current workpackageId
      if (workpackageId !== selectedWorkpackageId) {
        return;
      }

      if (jobType === 'run_refresh_similarity_groups') {
        this.runningRefresh = false;
      } else if (jobType === 'refresh_cross_elasticity_parameters') {
        this.runningXEORefresh = false;
      }

      this.gridApi.hideOverlay();

      // Reload grid data after refresh
      await Promise.all([this.fetchGridData(), this.fetchLastTriggeredInfo()]);
    },

    clearSwitchingPollingTimer() {
      if (this.switchingPollingTimerId) {
        clearTimeout(this.switchingPollingTimerId);
        this.switchingPollingTimerId = null;
      }
    },

    async fetchSwitchingCannGroupStatuses() {
      const [err, response] = await to(
        axios.get(`/api/product-switching/workpackage/${this.selectedWorkpackage._id}/status`)
      );

      if (err) {
        console.error('Failed to fetch switching statuses', err);
        return;
      }

      this.$set(this, 'switchingCannGroupStatuses', get(response, 'data.cannGroups', []));
    },

    stopSwitchingPolling() {
      this.runningSwitching = false;
      this.clearSwitchingPollingTimer();
    },

    /**
     * Poll for switching job status and update cann group statuses.
     * Polls continuously until the backend job reaches a terminal state (completed or failed).
     * No client-side timeout is enforced; polling relies on backend job termination.
     * @param {string} jobId - The job ID to poll for
     */
    async pollSwitchingJobStatus(jobId) {
      const [err, response] = await to(
        axios.get(
          `/api/product-switching/workpackage/${this.selectedWorkpackage._id}/job/${jobId}/status`
        )
      );

      if (err) {
        console.error('Poll switching job status error:', err);
        this.stopSwitchingPolling();
        if (this.gridApi) {
          this.gridApi.hideOverlay();
        }
        return;
      }

      this.latestSwitchingJobStatus = response.data;
      // Update cann group statuses from the job status polling
      // Use $set to ensure Vue properly tracks the reactive change
      const updatedStatuses = this.upsertCannGroupStatus({
        cannGroups: this.switchingCannGroupStatuses,
        jobStatus: response.data,
      });

      this.$set(this, 'switchingCannGroupStatuses', updatedStatuses);

      const status = get(response, 'data.status');
      if (status === 'completed') {
        // On terminal states, refresh full workpackage snapshot to stay fully consistent.
        await this.fetchSwitchingCannGroupStatuses();
        this.stopSwitchingPolling();
        if (this.gridApi) {
          this.gridApi.hideOverlay();
        }
        await this.fetchGridData();
        return;
      }

      if (status === 'failed') {
        // On terminal states, refresh full workpackage snapshot to stay fully consistent.
        await this.fetchSwitchingCannGroupStatuses();
        this.stopSwitchingPolling();
        if (this.gridApi) {
          this.gridApi.hideOverlay();
        }
        return;
      }

      this.switchingPollingTimerId = setTimeout(() => {
        this.pollSwitchingJobStatus(jobId);
      }, switchingPollingIntervalMs);
    },

    async fetchFreshSwitchingJobStatus(jobId) {
      const [err, response] = await to(
        axios.get(
          `/api/product-switching/workpackage/${this.selectedWorkpackage._id}/job/${jobId}/status`
        )
      );

      if (err) {
        console.error('Failed to fetch fresh switching job status:', err);
        this.addNotification({
          id: `${new Date()}freshSwitchingStatusFailed`,
          jobStatus: 'warning',
          baseTranslation: 'notifications.switchingStatusSyncFailed',
        });
        return null;
      }

      return response.data;
    },

    /**
     * Attempt to resume polling for a given job.
     * Fetches fresh status, updates state, and handles terminal vs. active cases.
     * @param {string} jobId - The job ID to resume polling for
     * @returns {Promise<boolean>} - true if polling was resumed, false if job is terminal/inactive
     */
    async tryResumeSwitchingPolling(jobId) {
      this.runningSwitching = true;

      // Fetch fresh status from backend
      const freshStatus = await this.fetchFreshSwitchingJobStatus(jobId);

      if (!freshStatus) {
        // Fetch failed, but resume polling anyway (fallback to cached state)
        this.pollSwitchingJobStatus(jobId);
        return true;
      }

      // Update state with fresh data
      this.latestSwitchingJobStatus = freshStatus;
      const updatedStatuses = this.upsertCannGroupStatus({
        cannGroups: this.switchingCannGroupStatuses,
        jobStatus: freshStatus,
      });
      this.$set(this, 'switchingCannGroupStatuses', updatedStatuses);

      // If job is already in terminal state, clean up and don't resume polling
      if (freshStatus.status === 'completed' || freshStatus.status === 'failed') {
        await this.fetchSwitchingCannGroupStatuses();
        this.stopSwitchingPolling();
        if (this.gridApi) {
          this.gridApi.hideOverlay();
        }
        await this.fetchGridData();
        return false;
      }

      // Job is still active, resume polling
      this.pollSwitchingJobStatus(jobId);
      return true;
    },

    async resumeSwitchingPollingIfNeeded() {
      // Detect active polling jobs by checking cann group statuses.
      // If any cann groups are in an unterminated state (not completed or failed),
      // resume polling for the associated job.
      if (!this.hasUnterminatedCannGroups()) {
        return;
      }

      const activeJobId = this.getActiveJobIdFromCannGroups();
      if (activeJobId) {
        // Auto-recover polling for detected in-progress job
        await this.tryResumeSwitchingPolling(activeJobId);
      }
    },

    async generateSwitchingMatrix() {
      this.gridApi.showLoadingOverlay();
      this.runningSwitching = true;
      const [err, res] = await to(
        axios.post(
          `/api/product-switching/workpackage/${this.selectedWorkpackage._id}/generate-switching`
        )
      );

      if (err) {
        this.runningSwitching = false;
        this.gridApi.hideOverlay();
        console.error('Failed to generate switching matrix', err);
        return;
      }

      // Success: jobId has been created and submitted
      console.info('Switching matrix generation job submitted successfully', res);

      const jobId = get(res, 'data.jobId');
      if (!jobId) {
        this.stopSwitchingPolling();
        this.gridApi.hideOverlay();
        return;
      }

      this.pollSwitchingJobStatus(jobId);
    },

    async isRefreshRunning() {
      const [err, res] = await to(
        Promise.all([
          axios.get(`/api/similarity-groups/${this.selectedWorkpackage._id}/refresh/status`, {
            params: { jobType: 'run_refresh_similarity_groups' },
          }),
          axios.get(`/api/similarity-groups/${this.selectedWorkpackage._id}/refresh/status`, {
            params: { jobType: 'refresh_cross_elasticity_parameters' },
          }),
        ])
      );
      if (err) {
        // Gracefully handle API failures, assume refresh is not running
        console.warn('Failed to check refresh status:', err.message);
        this.runningRefresh = false;
        this.runningXEORefresh = false;
        console.error(err.message);
        return { isRefreshRunning: false, isXEORefreshRunning: false };
      }
      const [isRefreshRunning, isXEORefreshRunning] = res.map(r => r.data);
      this.runningRefresh = isRefreshRunning;
      this.runningXEORefresh = isXEORefreshRunning;
      return { isRefreshRunning, isXEORefreshRunning };
    },

    async fetchLastTriggeredInfo() {
      try {
        const workpackageId = this.selectedWorkpackage._id;

        this.isLastTriggeredLoading = true;

        const getParams = fullEventName => {
          return {
            where: {
              fullEventName,
              $and: [{ entityIds: { $in: [workpackageId] } }],
            },
            sortBy: 'date',
            sortDirection: descending,
            limit: 1,
          };
        };

        // Build query parameters
        const sgParams = getParams(Events.similarityGroups.refresh);
        const xeoParams = getParams(Events.similarityGroups.refreshXeoParams);

        // Make API requests
        const [{ data: sgAuditLogs }, { data: xeoAuditLogs }] = await Promise.all([
          axios.get('/api/audit-log', { params: sgParams }),
          axios.get('/api/audit-log', { params: xeoParams }),
        ]);

        this.lastTriggeredInfo = get(sgAuditLogs, 0, {});
        this.lastTriggeredXEOInfo = get(xeoAuditLogs, 0, {});
      } catch (error) {
        console.error('Error fetching last triggered info:', error);
        this.lastTriggeredInfo = {};
        this.lastTriggeredXEOInfo = {};
      } finally {
        this.isLastTriggeredLoading = false;
      }
    },

    formatDate(date) {
      return moment(date).format(hoursMinutesDayMonthYearSeparatedFormat);
    },

    async exportSimilarityGroups() {
      this.gridApi.exportDataAsExcel({
        skipColumnGroupHeaders: true,
        fileName: 'similarity-groups',
      });
    },

    fillOperation(params) {
      // mod to get remainder. e.g. if there are 2 values, alternate between them apply to each cell in selection
      const fillValue = params.initialValues[params.currentIndex % params.initialValues.length];

      // get available values for cell editor dropdown
      const availableValues = params.column.colDef.cellEditorParams({
        data: params.rowNode.data,
      });

      if (!availableValues.values.includes(fillValue)) {
        // User can reassign products to existing similarity groups
        // as long as they are within the same cann group and have the same unit of measure.
        console.warn(
          `Cannot fill ${fillValue} because it is not in the available values: ${
            availableValues.values
          }`
        );
        // keep original value
        return params.currentCellValue;
      }

      return fillValue;
    },

    hasDiff(params) {
      const { productKey } = params.data;
      const originalValue = get(this.originalProductsByKey[productKey], params.colDef.field);

      if (isNil(originalValue) || isNil(params.value)) {
        this.$delete(this.stateDiff, productKey);
        return false;
      }

      if (originalValue !== params.value) {
        this.$set(this.stateDiff, productKey, params.value);
        return true;
      }

      this.$delete(this.stateDiff, productKey);
      return false;
    },

    async refreshXEOParams(useProductSwitching = false) {
      this.gridApi.showLoadingOverlay();
      this.runningXEORefresh = true;
      const [err] = await to(
        axios.post(`/api/similarity-groups/${this.selectedWorkpackage._id}/refreshXeoParams`, {
          use_product_switching_matrices: useProductSwitching,
        })
      );

      if (err) {
        this.runningXEORefresh = false;
        this.gridApi.hideOverlay();
      }
    },

    /* -------------------------------------------------- *
     *           Edit dialogs functions                   *
     * -------------------------------------------------- */
    openEditDialog() {
      this.isEditDialogOpen = true;
    },

    cancelEdit() {
      this.isEditDialogOpen = false;
      this.isEditConfirmDialogOpen = false;
      this.resetForm();
    },

    openEditConfirm() {
      this.isEditDialogOpen = false;
      this.isEditConfirmDialogOpen = true;
    },

    async confirmEdit() {
      this.isEditConfirmDialogOpen = false;
      this.gridApi.showLoadingOverlay();
      const url = `/api/similarity-groups/${this.selectedWorkpackage._id}/${
        this.selectedSimilarityGroupKey
      }`;
      const [err] = await to(
        axios.patch(url, {
          similarityGroupName: this.newSimilarityGroupName,
        })
      );

      if (!err) {
        this.fetchGridData();
      } else {
        this.gridApi.hideOverlay();
        this.addNotification({
          id: `${new Date()}renameSimilarityGroup`,
          jobStatus: 'failed',
          baseTranslation: 'notifications.renameSimilarityGroup',
        });
      }

      this.resetForm();
    },

    resetForm() {
      this.selectedSimilarityGroupKey = '';
      this.newSimilarityGroupName = '';
      this.$refs.editForm.resetValidation();
    },

    /* -------------------------------------------------- *
     *           Create dialog functions                  *
     * -------------------------------------------------- */
    /**
     * Handle cell value change for similarity group dropdown
     * Opens create dialog if flag was set by valueSetter
     */
    openCreateDialog(product) {
      this.createDialogProduct = product;
      this.newGroupName = '';
      this.isCreateDialogOpen = true;
    },

    cancelCreate() {
      this.isCreateDialogOpen = false;
      this.createDialogProduct = null;
      this.newGroupName = '';
      if (this.$refs.createForm) {
        this.$refs.createForm.resetValidation();
      }
    },

    async confirmCreate() {
      const product = this.createDialogProduct;
      const cannGroupId = get(product, 'cannGroup.cannGroupId');
      const uom = product.contentUnitOfMeasure;
      const cluster = this.countUniqueSimilarityGroups(cannGroupId, uom);

      // Generate similarity group key
      const hashInput = `${cluster}_${cannGroupId}_${uom}`;
      const [hashErr, similarityGroupKey] = await to(this.generateSimilarityGroupHash(hashInput));

      if (hashErr) {
        console.error('Error generating hash:', hashErr);
        this.addNotification({
          id: `${new Date()}createSimilarityGroupHash`,
          jobStatus: 'failed',
          baseTranslation: 'notifications.createSimilarityGroup',
        });
        return;
      }

      // Update product in grid
      const productIndex = this.products.findIndex(p => p.productKey === product.productKey);
      if (productIndex !== -1) {
        this.$set(this.products[productIndex], 'similarityGroup', {
          similarityGroupKey,
          similarityGroupDescription: this.newGroupName,
          cluster,
          lastUpdated: moment.utc().toISOString(),
          lastUpdatedBy: this.profile.username,
        });

        // Add to stateDiff to mark as changed
        this.$set(this.stateDiff, product.productKey, similarityGroupKey);

        // Refresh grid display
        this.gridApi.refreshCells();
      }

      this.cancelCreate();
    },

    /* -------------------------------------------------- *
     *           Validation functions                     *
     * -------------------------------------------------- */
    valueRequired(v) {
      return !!v || v === 0 || this.$t('validationErrors.fieldRequired');
    },

    isNameUnique(v) {
      const existingNames = Object.values(this.similarityGroups).map(sg =>
        toLower(sg.similarityGroupDescription)
      );
      return (
        !existingNames.includes(toLower(trim(v))) ||
        this.$t('validationErrors.similarityGroupNameExists')
      );
    },

    isCreateNameUnique(v) {
      if (!this.createDialogProduct) {
        return true;
      }

      const pricingGroupId = get(
        this.createDialogProduct,
        `hierarchy.${this.pricingGroupLevel}.id`
      );

      // Get all products in the same pricing group
      const productsInSamePricingGroup = this.products.filter(
        product =>
          get(product, `hierarchy.${this.pricingGroupLevel}.id`) === pricingGroupId &&
          get(product, 'similarityGroup.similarityGroupDescription')
      );

      const existingNamesInPricingGroup = productsInSamePricingGroup.map(product =>
        toLower(get(product, 'similarityGroup.similarityGroupDescription', ''))
      );

      return (
        !existingNamesInPricingGroup.includes(toLower(trim(v))) ||
        this.$tkey('notifications.nameConflict')
      );
    },

    /* -------------------------------------------------- *
     *           Page actions functions                   *
     * -------------------------------------------------- */
    async onSave() {
      this.isSaving = true;
      this.gridApi.showLoadingOverlay();
      const updates = map(this.stateDiff, (similarityGroupKey, productKey) => ({
        productKey,
        ...this.similarityGroups[similarityGroupKey],
      }));

      const [err] = await to(
        axios.post(`/api/similarity-groups/${this.selectedWorkpackage._id}/reassign`, {
          updates,
        })
      );

      if (!err) {
        this.stateDiff = {};
        this.fetchGridData();
      } else {
        this.gridApi.hideOverlay();
        this.addNotification({
          id: `${new Date()}reassignSimilarityGroups`,
          jobStatus: 'failed',
          baseTranslation: 'notifications.reassignSimilarityGroups',
        });
      }
      this.isSaving = false;
    },

    onDiscard() {
      this.setGridData(this.originalProducts);
    },

    getAttributeValue(params, attributeToGet) {
      const attribute = params.data.attributes.find(
        attr => attr.attributeKey === attributeToGet.attributeKey
      );
      return attribute ? attribute.attributeValue : null;
    },

    handleColumnsPanelClosed() {
      this.hideAttributeColumnsOverMaxCount();
      const state = this.gridApi.getColumnState();
      // Check if we need to fetch additional attributes
      const shouldRefetch = !this.additionalAttributes.isSubsetOf(this.getGridDisplayedAttributes);
      this.setGridDisplayedAttributes(new Set(this.additionalAttributes));
      // preserve user selected columns on sidebar close
      // because setGridDisplayedAttributes above resets visibility to default state
      this.$nextTick(() => {
        this.gridApi.applyColumnState({ state });
      });
      if (shouldRefetch) {
        this.fetchGridData();
      }
    },

    onColumnVisible(event) {
      this.updateAdditionalAttributes(event.columns);
    },

    setFilterRules(filterRules) {
      this.setSelectedFilter({ filterName: 'retailAttributesFilter', filterValue: filterRules });
      this.fetchGridData();
    },

    upsertCannGroupStatus({ cannGroups, jobStatus }) {
      const statusPayload = jobStatus || {};
      const cannGroupsFromJob = statusPayload.cannGroups || [];

      // If jobStatus has cannGroups, use them as the source of truth
      if (cannGroupsFromJob.length > 0) {
        // Map jobStatus groups to ensure they have all required fields
        const result = cannGroupsFromJob
          .filter(cg => cg.cannGroupId && cg.cannGroupName)
          .map(cg => ({
            cannGroupId: cg.cannGroupId,
            cannGroupName: cg.cannGroupName,
            status: cg.status,
            jobId: cg.jobId,
            updatedAt: new Date(),
          }))
          .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

        return result;
      }

      // Fallback: if no cannGroups in jobStatus, return current groups unchanged
      return cannGroups || [];
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';
@import '@style/mixins/_general.scss';
::v-deep {
  .ag-similarity-groups-grid {
    /* ==========================================
       Cell Styles
       Base styles for grid cells
       ========================================== */
    .ag-cell {
      display: inline-block;
      border-bottom: none;
      border-top: none;

      &.font-weight-bold {
        font-weight: bold;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
    }

    /* ==========================================
       Row Styles
       Controls the appearance of grid rows
       ========================================== */
    .ag-row {
      border-bottom: none;
      border-top: none;

      &.ag-row-even {
        background: $custom-ag-grid-even-rows-background-color;
      }

      &.ag-row-odd {
        background: $custom-ag-grid-odd-rows-background-color;
      }
    }

    .ag-column-panel::before {
      @include column-panel-data-load-message;
    }

    .ag-side-buttons {
      display: none;
    }
  }
}

.similarity-groups-actions {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  background-color: white;
  border-bottom: 1px solid $grid-header-footer-border-color;
}

.diff-background {
  background-color: $ag-grid-edit-background-color;
}

.workflow-controls {
  .workflow-buttons {
    align-items: center;
  }

  .workflow-info-row {
    margin-top: 4px;
  }
}

.switching-status-tooltip {
  ::v-deep {
    .tooltip-container {
      .tooltip-text {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        text-align: left;
      }
    }
  }
}
</style>
